/**
 * UniquePopovers — Designer-side popover wrappers for the add + drop UNIQUE
 * forms. Sqlite renders disabled triggers with the recreate-path tooltip; the
 * form bodies are never mounted on sqlite. Mirrors `ForeignKeyPopovers` from
 * Slice B2.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getAddUniqueConstraintUnsupportedTooltip,
  isAddUniqueConstraintSupported,
} from '../../edit/edits/addUniqueConstraint';
import {
  getDropUniqueConstraintUnsupportedTooltip,
  isDropUniqueConstraintSupported,
} from '../../edit/edits/dropUniqueConstraint';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { UniqueAddFormBody } from './UniqueAddFormBody';
import { UniqueDropFormBody } from './UniqueDropFormBody';

export interface UniqueAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function UniqueAddPopover(props: UniqueAddPopoverProps) {
  const { editContext, testId = 'unique-add-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddUniqueConstraintSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddUniqueConstraintUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Unique
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add unique constraint"
        >
          <Plus className="h-3.5 w-3.5" />
          Unique
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <UniqueAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface UniqueDropPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function UniqueDropPopover(props: UniqueDropPopoverProps) {
  const { editContext, testId = 'unique-drop-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropUniqueConstraintSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropUniqueConstraintUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Drop unique
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop unique constraint"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Drop unique
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-80"
        data-testid={testId}
      >
        {open ? (
          <UniqueDropFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
